import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../core/api";
import { LOGOUT_MSG_KEY, saveLogin } from "../../core/auth";
import { fetchUserConfig, firstAllowedPath } from "../../utils/roleHelper";
import Icon from "../../components/Icon/Icon";
import { BrandMark } from "../../components/Brand/Brand";
import RegisterForm from "./RegisterForm";
import ForgotPasswordForm from "./ForgotPasswordForm";
import GoogleButton from "./GoogleButton";
import { APP_NAME, GOOGLE_CLIENT_ID, URL_PRIVACY, URL_TERMINI } from "../../config/app";
import s from "./Login.module.scss";

// Consigli a rotazione nel pannello del marchio (come il login di gestionale-gls).
const TIPS = [
  { title: "Il cliente segna, lo studio elabora", text: "Ogni cliente ha il suo link per le presenze: niente account, funziona dal telefono. A fine mese preme «Invia allo studio» e voi lo trovate pronto." },
  { title: "Il foglio HR è già fatto", text: "Ordinario, straordinario, ferie, permessi e malattia per ogni giorno, con i totali: si esporta in Excel, PDF o per Paghe Infinity." },
  { title: "Carichi i documenti, compilo io", text: "Carte d'identità, contratti, buste paga, certificati: la lettura automatica estrae i dati e ti mostra un riepilogo da confermare." },
  { title: "Nessuna scadenza dimenticata", text: "Documenti, contratti a termine, periodi di prova e qualunque altra scadenza, con i promemoria a chi se ne occupa." },
  { title: "Ognuno vede i suoi clienti", text: "Ruoli come Addetto paghe o Consulente decidono sezioni, dati economici e sanitari, e i clienti che ognuno segue." },
];

export default function Login() {
  const navigate = useNavigate();
  const [view, setView] = useState("login");
  const [data, setData] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [tip, setTip] = useState(0);
  const [googleProfile, setGoogleProfile] = useState(null);

  useEffect(() => {
    const msg = localStorage.getItem(LOGOUT_MSG_KEY);
    if (msg) {
      setNotice(msg);
      localStorage.removeItem(LOGOUT_MSG_KEY);
    }
    const id = setInterval(() => setTip((i) => (i + 1) % TIPS.length), 6500);
    return () => clearInterval(id);
  }, []);

  const afterAuth = async (res) => {
    saveLogin({ token: res.token, nomeCompleto: res.nomeCompleto, email: res.email, idAzienda: res.idAzienda });
    if (res.pendingApproval || !res.idAzienda) {
      navigate("/pending-approval", { replace: true });
      return;
    }
    const cfg = await fetchUserConfig(true);
    navigate(firstAllowedPath(cfg), { replace: true });
  };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/login", data);
      await afterAuth(res.data);
    } catch (err) {
      setLoading(false);
      const st = err?.response?.status;
      if (st === 400) setError("Email o password errati.");
      else if (st === 429) setError(err?.response?.data?.message || "Troppi tentativi, riprova tra 15 minuti.");
      else if (!err?.response) setError("Impossibile contattare il server. Controlla che il backend sia avviato e riprova.");
      else setError("Accesso non riuscito. Riprova tra qualche istante.");
    }
  };

  return (
    <div className={s.login}>
      <aside className={s.hero}>
        <div className={s.heroGrid} aria-hidden="true" />
        <div className={s.heroGlow} aria-hidden="true" />
        <div className={s.heroTop}>
          <span className={s.heroLogo}>
            <BrandMark size={34} inverted />
          </span>
          <div>
            <div className={s.heroProduct}>{APP_NAME}</div>
            <div className={s.heroEyebrow}>Presenze, dipendenti e scadenze dei clienti</div>
          </div>
        </div>
        <div className={s.heroCenter}>
          <h1 className={s.heroHeadline}>Le presenze dei clienti, pronte per le paghe.</h1>
          <p className={s.heroLead}>I clienti segnano le presenze da un link, voi le trovate nel foglio HR insieme a dipendenti, documenti e scadenze. Senza fogli Excel che vanno e vengono.</p>
          <div className={s.tipCard}>
            <div className={s.tipCount}>
              {tip + 1} / {TIPS.length}
            </div>
            <div className={s.tipBody} key={tip}>
              <div className={s.tipTitle}>{TIPS[tip].title}</div>
              <div className={s.tipText}>{TIPS[tip].text}</div>
            </div>
            <div className={s.tipDots}>
              {TIPS.map((_, i) => (
                <button key={i} type="button" className={`${s.tipDot} ${i === tip ? s.tipDotActive : ""}`} onClick={() => setTip(i)} aria-label={`Consiglio ${i + 1}`} />
              ))}
            </div>
          </div>
        </div>
        <div className={s.heroFooter}>30 giorni di prova gratuita · nessuna carta richiesta · utenti illimitati</div>
      </aside>

      <main className={s.panel}>
        <div className={s.mobileBrand}>
          <BrandMark size={34} />
          <span>{APP_NAME}</span>
        </div>

        {view === "login" && (
          <div className={s.formBox}>
            <h2 className={s.welcome}>Bentornato</h2>
            <p className={s.subtitle}>Accedi al tuo studio</p>
            {GOOGLE_CLIENT_ID && (
              <>
                <GoogleButton
                  onLogin={afterAuth}
                  onNeedsRegistration={(profile) => {
                    setGoogleProfile(profile);
                    setNotice("Completa la registrazione per creare il tuo account.");
                    setView("register");
                  }}
                  onError={setError}
                />
                <div className={s.divider}>
                  <span>oppure</span>
                </div>
              </>
            )}
            <form onSubmit={login} className={s.form}>
              <div className={s.inputWrap}>
                <Icon name="mail" size={18} />
                <input type="email" autoComplete="username" placeholder="Email" value={data.username} onChange={(e) => setData((d) => ({ ...d, username: e.target.value }))} />
              </div>
              <div className={s.inputWrap}>
                <Icon name="lock" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Password"
                  value={data.password}
                  onChange={(e) => setData((d) => ({ ...d, password: e.target.value }))}
                />
                <button type="button" className={s.eye} onClick={() => setShowPassword((v) => !v)} aria-label="Mostra password">
                  <Icon name={showPassword ? "eyeOff" : "eye"} size={18} />
                </button>
              </div>
              <div className={s.forgotRow}>
                <button type="button" className="pr-link-btn" onClick={() => setView("forgot")}>
                  Password dimenticata?
                </button>
              </div>
              {notice && <div className={s.notice}>{notice}</div>}
              {error && <div className="pr-error">{error}</div>}
              <button type="submit" className={s.mainButton} disabled={loading}>
                {loading ? "Accesso in corso..." : "Accedi"}
              </button>
            </form>
            <p className={s.prompt}>
              Non hai un account?{" "}
              <button type="button" className="pr-link-btn" onClick={() => setView("register")}>
                Prova gratis per 30 giorni
              </button>
            </p>
            <p className={s.prompt}>
              Hai un codice dello studio?{" "}
              <button type="button" className="pr-link-btn" onClick={() => {
                  setError("");
                  setView("code");
                }}>
                Inseriscilo qui
              </button>
            </p>
          </div>
        )}

        {view === "code" && (
          <div className={s.formBox}>
            <h2 className={s.welcome}>Codice dello studio</h2>
            <p className={s.subtitle}>Inserisci il codice di 8 caratteri che ti ha dato l'amministratore del tuo studio.</p>
            <form
              className={s.form}
              onSubmit={(e) => {
                e.preventDefault();
                const c = code.trim().toUpperCase();
                if (c.length < 6) return setError("Inserisci un codice valido.");
                return navigate(`/invito/c/${encodeURIComponent(c)}`);
              }}
            >
              <div className={s.inputWrap}>
                <Icon name="tag" size={18} />
                <input className={s.codeInput} placeholder="ES: K7QH3M2P" maxLength={16} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} autoFocus />
              </div>
              {error && <div className="pr-error">{error}</div>}
              <button type="submit" className={s.mainButton}>
                Continua
              </button>
            </form>
            <button type="button" className={s.back} onClick={() => {
                setView("login");
                setError("");
              }}>
              <Icon name="arrowLeft" size={16} /> Torna all'accesso
            </button>
          </div>
        )}

        {view === "forgot" && <ForgotPasswordForm onBack={() => setView("login")} />}

        {view === "register" && (
          <RegisterForm
            googleProfile={googleProfile}
            onBack={() => {
              setView("login");
              setGoogleProfile(null);
            }}
            onSuccess={afterAuth}
          />
        )}

        <nav className={s.legalLinks} aria-label="Documenti legali">
          <a href={URL_TERMINI} target="_blank" rel="noopener noreferrer">
            Termini di servizio
          </a>
          <a href={URL_PRIVACY} target="_blank" rel="noopener noreferrer">
            Privacy
          </a>
        </nav>
      </main>
    </div>
  );
}
